import { defineComponent as _defineComponent } from 'vue'
import {ref, reactive, computed, onMounted, defineAsyncComponent, Ref} from "vue";
import { useTranslations } from "@/hooks/useTranslations";
import VBreadcrumbs from "@/components/vBreadcrumbs.vue";
import UserDetails from "@/models/User/UserDetails";
import {useStore} from "@/hooks/useStore";
import { TRANSLATIONS_FETCH } from "@/store/ActionTypes";
import {useRoute, useRouter} from "vue-router/composables";
import VListAdditionalCost from "@/components/costing/vListAdditionalCost.vue";
import VListIngredientCost from "@/components/costing/vListIngredientCost.vue";
import VListRecipeCost from "@/components/costing/vListRecipeCost.vue";
import VModifyIngredientCost from "@/components/costing/vModifyIngredientCost.vue";
import VModifyAdditionalCost from "@/components/costing/vModifyAdditionalCost.vue";
import {IngredientCost} from "@/models/IngredientCost/IngredientCost";
import {AdditionalCost} from "@/models/AdditionalCost/AdditionalCost";
import {Invoice} from "@/models/Invoice/Invoice";
import invoiceRepository from "@/utils/API/InvoiceRepository";
import {debounce, round} from "@/utils/Helpers";
import {useNotify} from "@/hooks/useNotify";
import router from "@/router";
import VSelectFx from "@/components/vSelectFx.vue";
import AdditionalCostRepository from "@/utils/API/AdditionalCostRepository";
import VAdditionalMeasurementModal from "@/components/costing/vAdditionalMeasurementModal.vue";
import {ItemCostMeasurement} from "@/models/RecipeInvoice/ItemCostMeasurement";
import {AxiosResponse} from "axios";
import {ListResult} from "@/utils/API/ResultTypes/ListResult";
import {Measurement} from "@/models/Measurement/Measurement";
import MeasurementRepository from "@/utils/API/MeasurementRepository";
import {MeasurementCategory} from "@/models/Measurement/MeasurementCategory";
import {IngredientCostMeasurement} from "@/models/IngredientCost/IngredientCostMeasurement";

interface InvoiceGridRow {
  meta: { id: number; link: string };
  e1: string | number;
  e2: number;
  e3: string;
  e4: string;
  e5: string | number;
  e6: string;
  e7: Partial<ItemCostMeasurement>[];
  type: "recipe" | "ingredient" | "additional";
  saving?: boolean;
}

// const vSelectFx = defineAsyncComponent(() => import("@/components/vSelectFx.vue"));

export default /*#__PURE__*/_defineComponent({
  __name: 'RecipeInvoiceManagement',
  setup(__props) {

const store = useStore();
const recipeInvoice = ref<Invoice>({
  id: 0,
  name: '',
  restaurantId: 0,
  createdOn: new Date(),
  recipeCosts: [],
  ingredientCosts: [],
  additionalCosts: [],
});
const showAddingMeasurementrModal = ref(false);

let translations = ref(useTranslations());
let showModifyRecipeCostModal = ref(false);
let showModifyIngredientCostModal = ref(false);
let showModifyAdditionalCostModal = ref(false);
let showListAdditionalCostModal = ref(false);
let showListIngredientCostModal = ref(false);
let showListRecipeCostModal = ref(false);

let selectedIngredientCost: Ref<IngredientCost> = ref(null);
let selectedAdditionalCost: Ref<AdditionalCost> = ref(null);

const massMeasurements = ref<IngredientCostMeasurement[]>([]);
const notify = useNotify();
const route = useRoute();

const loading = ref(false);
const deletingModal = reactive({
  isOpen: false,
  id: 0,
});

const tableHeaders = computed(() => [
  { name: translate("rec_invoice_item_name"), key: "ItemName", sortable: true },
  { name: translate("ingr-cost-table-vendor"), key: "SupplierName", sortable: true },
  { name: translate("ingr-cost-table-quantity"), key: "Quantity", sortable: true },
  { name: translate("ingr-cost-table-measurement"), key: "Measurement", sortable: true },
  { name: translate("rec_invoice_cost_type"), key: "CostType" },
  { name: translate("ingr-cost-table-cost"), key: "Cost" },
  { name: '', key: "type" },
]);

const userDetails:UserDetails = null;

const ingredientCostIdsInInvoice = computed(() => {
  if (!recipeInvoice.value || !recipeInvoice.value.ingredientCosts)
    return [];

  return recipeInvoice.value.ingredientCosts.map((c) => c.ingredientCostId);
});

const additionalCostIdsInInvoice = computed(() => {
  if (!recipeInvoice.value || !recipeInvoice.value.additionalCosts)
    return [];

  return recipeInvoice.value.additionalCosts.map((c) => c.additionalCostId);
});

const recipeCostIdsInInvoice = computed(() => {
  if (!recipeInvoice.value || !recipeInvoice.value.recipeCosts)
    return [];

  return recipeInvoice.value.recipeCosts.map((c) => c.recipeCostId);
});

const recipeInvoiceForGrid = computed(() => {
  const invoice = recipeInvoice.value;
  if (!invoice) return [];
  const rows: InvoiceGridRow[] = [];

  // Recipe Costs
  (invoice.recipeCosts || []).forEach((c) => {
    console.log("Recipe Costs: ", c);
    rows.push({
      meta: {
        id: c.recipeCostId,
        link: (c.recipeCost)
          ? c.recipeName
          : "Recipe",
        measurementId: c.recipeCostMeasurement.id,
      },
      e1: "N/A", // no vendor for recipe
      e2: c.quantity,
      e3: c.recipeCostMeasurement.measurement.name,
      e4: "Recipe",
      e5: Math.abs(round(c.costAtTimeOfInvoice, 2)),
      e6: c.recipeCostMeasurement.measurement.name,
      e7: c.recipeCostMeasurements,
      type: "recipe",
    });
  });

  // Ingredient Costs
  (invoice.ingredientCosts || []).forEach((c) => {
    console.log("Ingredient Costs: ", c);
    rows.push({
      meta: {
        id: c.ingredientCostId,
        link: (c.ingredientCost && c.ingredientCost.name)
          ? c.ingredientCost.name
          : "Ingredient",
        measurementId: c.ingredientCostMeasurement.id,
      },
      e1: (c.ingredientCost && c.ingredientCost.vendor && c.ingredientCost.vendor.name)
        ? c.ingredientCost.vendor.name
        : "-",
      e2: c.quantity,
      e3: c.ingredientCostMeasurement.measurement.name,
      e4: "Ingredient",
      e5: Math.abs(round(c.costAtTimeOfInvoice, 2)),
      e6: c.ingredientCostMeasurement.measurement.name,
      e7: c.ingredientCostMeasurements,
      type: "ingredient",
    });
  });

  // Additional Costs
  (invoice.additionalCosts || []).forEach((c) => {
    console.log("Additional Costs: ", c);
    rows.push({
      meta: {
        id: c.additionalCostId,
        link: (c.additionalCost && c.additionalCost.name)
          ? c.additionalCost.name
          : "Additional",
        measurementId: c.additionalCostMeasurement.id,
      },
      e1: (c.additionalCost && c.additionalCost.vendor && c.additionalCost.vendor.name)
        ? c.additionalCost.vendor.name
        : "-",
      e2: c.quantity,
      e3: c.additionalCostMeasurement.measurement.name,
      e4: "Additional",
      e5: Math.abs(round(c.costAtTimeOfInvoice, 2)),
      e6: c.additionalCostMeasurement.measurement.name,
      e7: c.additionalCostMeasurements,
      type: "additional",
    });
  });

  return rows;
});

function exportFile() {
  if (route.name === "recipeInvoiceAdd" && recipeInvoice.value.id === 0) {
    return;
  }
  let invoiceId = recipeInvoice.value.id;
  const invoiceIdFromRoute = Number(route.params.id);

  if(recipeInvoice.value.id == 0){
    if (!isNaN(invoiceIdFromRoute)) {
      invoiceId = invoiceIdFromRoute;
    }
    else {
      return;
    }
  }

  return `${process.env.VUE_APP_BASE_DOMAIN}/api/Processes/Invoice/ExportInvoiceBreakdown/${invoiceId}`;
}

function translate(key: string) {
  return translations.value.get(key);
}

function onEditRow(row: InvoiceGridRow) {
  switch (row.type) {
  case "recipe":
    showModifyRecipeCostModal.value = true;
    goOnEditRecipeCostPage(row.meta.id);
    break;
  case "ingredient":
    fetchIngredientCostById(row.meta.id);
    showModifyIngredientCostModal.value = true;
    break;
  case "additional":
    fetchAdditionalCostById(row.meta.id);
    showModifyAdditionalCostModal.value = true;
    break;
  }
}

function openAddMeasurementModal() {
  showAddingMeasurementrModal.value = true;
}

function handleSaveMeasurements(newMeasurements: any[]) {}

async function fetchAdditionalCostById(id: number) {
  const response = await AdditionalCostRepository.getAdditionalCostById(id);
  selectedAdditionalCost.value = response.data;
}

async function fetchIngredientCostById(id: number) {
  const response = await invoiceRepository.getIngredientCostById(id);
  selectedIngredientCost.value = response.data;
}

function goOnEditRecipeCostPage(recipeCostId: number) {
  router.push({
    name: "updateRecipeCost",
    params: { id: recipeCostId },
  });
}

function hideEditIngredientCostModal(){
  showModifyIngredientCostModal.value = false;
  requestInvoice();
}

function hideEditAdditionalCostModal(){
  showModifyAdditionalCostModal.value = false;
  requestInvoice();
}

function refreshIngredientCosts(updatedCosting: IngredientCost) {
  recipeInvoice.value.ingredientCosts.forEach((cost) => {
    if (cost.ingredientCostId === updatedCosting.id) {
      cost.ingredientCost = updatedCosting;
    }
  });
}

function refreshAdditionalCosts(updatedCosting: AdditionalCost) {
  recipeInvoice.value.additionalCosts.forEach((cost) => {
    if (cost.additionalCostId === updatedCosting.id) {
      cost.additionalCost = updatedCosting;
    }
  });
}

async function updateQuantity(row: InvoiceGridRow) {
  if (!recipeInvoice.value) return;

  try {
    row.saving = true;
    let response;

    switch (row.type) {
    case "recipe":
      response = await invoiceRepository.updateRecipeCostQuantity(
        recipeInvoice.value.id,
        row.meta.id,
        row.e2,
        recipeInvoice.value.recipeCosts
          .find((rc) => rc.recipeCostId === row.meta.id)
          .recipeCostMeasurements
          .find((rcm) => rcm.measurement.name === row.e6)
          .id
      );
      break;

    case "ingredient":
      response = await invoiceRepository.updateIngredientCostQuantity(
        recipeInvoice.value.id,
        row.meta.id,
        row.e2,
        recipeInvoice.value.ingredientCosts
          .find((ic) => ic.ingredientCostId === row.meta.id)
          .ingredientCostMeasurements
          .find((icbm) => icbm.measurement.name === row.e6)
          .id
      );
      break;

    case "additional":
      response = await invoiceRepository.updateAdditionalCostQuantity(
        recipeInvoice.value.id,
        row.meta.id,
        row.e2,
        recipeInvoice.value.additionalCosts
          .find((ac) => ac.additionalCostId === row.meta.id)
          .additionalCostMeasurements
          .find((acme) => acme.measurement.name === row.e6)
          .id
      );
      break;
    }

    if (response) {
      recipeInvoice.value = response.data;
      notify.success("Quantity updated.");
    }
  }
  catch (err) {
    notify.error("Failed to update quantity.");
  }
  finally {
    row.saving = false;
  }
}

function deleteCostFromInvoice(row: InvoiceGridRow) {
  switch (row.type) {
  case "recipe":
    removeRecipeCostFromInvoice(row.meta.id);
    break;
  case "ingredient":
    removeIngredientCostFromInvoice(row.meta.id);
    break;
  case "additional":
    removeAdditionalCostFromInvoice(row.meta.id);
    break;
  }
}

function tryInitialSave() {
  if (
    !recipeInvoice.value.id
    && recipeInvoice.value.name
  ) {
    saveInvoice();
  }
}

const totalCost = computed(() => {
  if (!recipeInvoice.value) return 0;

  let sum = 0;
  (recipeInvoice.value.recipeCosts || []).forEach((c) => { sum += Math.abs(c.costAtTimeOfInvoice) || 0; });
  (recipeInvoice.value.ingredientCosts || []).forEach((c) => { sum += Math.abs(c.costAtTimeOfInvoice) || 0; });
  (recipeInvoice.value.additionalCosts || []).forEach((c) => { sum += Math.abs(c.costAtTimeOfInvoice) || 0; });

  return sum;
});

const formattedTotalCost = computed(() =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(totalCost.value)
);

function addIngredientCost(){
  if (!recipeInvoice.value.name) {
    notify.error(translate("rec_invoice_name_required"));
    return;
  }

  showListIngredientCostModal.value = true;
}

function addAdditionalCost(){
  if (!recipeInvoice.value.name) {
    notify.error(translate("rec_invoice_name_required"));
    return;
  }

  showListAdditionalCostModal.value = true;
}

function addRecipeCost(){
  if (!recipeInvoice.value.name) {
    notify.error(translate("rec_invoice_name_required"));
    return;
  }

  showListRecipeCostModal.value = true;
}

function hideIngredientCostModal(){
  showListIngredientCostModal.value = false;
  requestInvoice();
}

function hideAdditionalCostModal(){
  showListAdditionalCostModal.value = false;
  requestInvoice();
}

function hideRecipeCostModal(){
  showListRecipeCostModal.value = false;
  requestInvoice();
}

async function addIngredientCostToInvoice(selectedIngredientCostId: number) {
  if (!recipeInvoice.value || recipeInvoice.value.id === 0) {
    notify.error("Please save the invoice first.");
    return;
  }

  try {
    const response = await invoiceRepository.addIngredientCost(
      recipeInvoice.value.id,
      selectedIngredientCostId
    );

    recipeInvoice.value = response.data;

    notify.success(translate("rec_invoice_saved_success"));
  }
  catch (error) {
    notify.error(translate("rec_invoice_save_error"));
  }
}

async function addAdditionalCostToInvoice(selectedAdditionalCostId: number){
  if (!recipeInvoice.value || recipeInvoice.value.id === 0) {
    notify.error("Please save the invoice first.");
    return;
  }

  try {
    const response = await invoiceRepository.addAdditionalCost(
      recipeInvoice.value.id,
      selectedAdditionalCostId
    );

    recipeInvoice.value = response.data;

    notify.success(translate("rec_invoice_saved_success"));
  }
  catch (error) {
    notify.error(translate("rec_invoice_save_error"));
  }
}

async function addRecipeCostToInvoice(selectedRecipeCostId : number){
  if (!recipeInvoice.value || recipeInvoice.value.id === 0) {
    notify.error("Please save the invoice first.");
    return;
  }

  try {
    const response = await invoiceRepository.addRecipeCost(
      recipeInvoice.value.id,
      selectedRecipeCostId
    );

    recipeInvoice.value = response.data;

    notify.success(translate("rec_invoice_saved_success"));
  }
  catch (error) {
    notify.error(translate("rec_invoice_save_error"));
  }
}

async function removeIngredientCostFromInvoice(ingredientCostId: number) {
  if (!recipeInvoice.value || recipeInvoice.value.id === 0) {
    notify.error("Please save the invoice first.");
    return;
  }

  try {
    const response = await invoiceRepository.removeIngredientCost(
      recipeInvoice.value.id,
      ingredientCostId
    );
    recipeInvoice.value = response.data;
    notify.success("Ingredient cost removed from invoice.");
  }
  catch (err) {
    notify.error("Failed to remove ingredient cost.");
  }
}

async function removeAdditionalCostFromInvoice(additionalCostId: number){
  if (!recipeInvoice.value || recipeInvoice.value.id === 0) {
    notify.error("Please save the invoice first.");
    return;
  }

  try {
    const response = await invoiceRepository.removeAdditionalCost(
      recipeInvoice.value.id,
      additionalCostId
    );
    recipeInvoice.value = response.data;
    notify.success("Ingredient cost removed from invoice.");
  }
  catch (err) {
    notify.error("Failed to remove ingredient cost.");
  }
}

async function removeRecipeCostFromInvoice(recipeCostId: number){
  if (!recipeInvoice.value || recipeInvoice.value.id === 0) {
    notify.error("Please save the invoice first.");
    return;
  }

  try {
    const response = await invoiceRepository.removeRecipeCost(
      recipeInvoice.value.id,
      recipeCostId
    );
    recipeInvoice.value = response.data;
    notify.success("Ingredient cost removed from invoice.");
  }
  catch (err) {
    notify.error("Failed to remove ingredient cost.");
  }
}

// Lifecycle Hooks
onMounted(async() => {
  try {
    await store.value.dispatch(TRANSLATIONS_FETCH, {
      category: "pages",
      collections: ["common", "ingredients", "recipe costing", "ingredient cost"],
    });
    document.title =
      translate("rec_invoice") + " " + translate("meta_title_postfix");
  }
  catch (error: any) {
  }
  await requestInvoice();
  await loadMeasurements();
});

async function requestInvoice() {
  loading.value = true;
  try {
    if (route.name === "recipeInvoiceAdd") {
      recipeInvoice.value = {
        id: 0,
        name: "",
        restaurantId: 0,
        createdOn: new Date(),
        recipeCosts: [],
        ingredientCosts: [],
        additionalCosts: [],
      };
    }
    else {
      // Edit existing invoice
      const invoiceId = Number(route.params.id);
      if (isNaN(invoiceId)) {
        notify.error("Invalid invoice id in route");
        return;
      }

      const response = await invoiceRepository.getInvoiceDetails(invoiceId);
      recipeInvoice.value = response.data;
    }
  }
  finally {
    loading.value = false;
  }
}

async function saveInvoice() {
  if (!recipeInvoice.value.name) {
    notify.error(translate("rec_invoice_name_required"));
    return;
  }

  try {
    let response;

    if (recipeInvoice.value.id === 0) {
      // Create new invoice
      response = await invoiceRepository.createInvoice(recipeInvoice.value.name);
      recipeInvoice.value = response.data;

      router.push({
        name: "recipeInvoiceEdit",
        params: { id: recipeInvoice.value.id },
      });
    }
    else {
      //Update existing invoice
      invoiceRepository.updateInvoice(
        recipeInvoice.value.id,
        recipeInvoice.value.name
      );

      router.push("/recipe-invoice");
      // recipeInvoice.value = response.data;
    }

    notify.success(translate("rec_invoice_saved_success"));
  }
  catch (error) {
    notify.error(error.response.data.message);
  }
}

async function loadMeasurements() {
  let massRes: AxiosResponse<ListResult<Measurement>>;
  try {
    massRes = await MeasurementRepository.listMeasurementsForCategory(
      MeasurementCategory.Mass
    );
  }

  catch (err) {
    console.error("Failed to load measurements", err);
  }

  const mass = massRes.data.result.map((x) => ({
    id: x.id,
    weightInGrams: x.conversionCoeficient,
    ingredientCostId: 0,
    createOn: null,
    measurementId: 0,
    measurement: x,
  }));

  const volRes = await MeasurementRepository.listMeasurementsForCategory(
    MeasurementCategory.Volume
  );

  const volume = volRes.data.result
    .filter((v) => !massRes.data.result.some((m) => m.id === v.id))
    .map((x) => ({
      id: x.id,
      weightInGrams: x.conversionCoeficient,
      createOn: null,
      ingredientCostId: 0,
      measurementId: 0,
      measurement: x,
    }));

  massMeasurements.value = mass.concat(volume);
}


return { __sfc: true,store, recipeInvoice, showAddingMeasurementrModal, translations, showModifyRecipeCostModal, showModifyIngredientCostModal, showModifyAdditionalCostModal, showListAdditionalCostModal, showListIngredientCostModal, showListRecipeCostModal, selectedIngredientCost, selectedAdditionalCost, massMeasurements, notify, route, loading, deletingModal, tableHeaders, userDetails, ingredientCostIdsInInvoice, additionalCostIdsInInvoice, recipeCostIdsInInvoice, recipeInvoiceForGrid, exportFile, translate, onEditRow, openAddMeasurementModal, handleSaveMeasurements, fetchAdditionalCostById, fetchIngredientCostById, goOnEditRecipeCostPage, hideEditIngredientCostModal, hideEditAdditionalCostModal, refreshIngredientCosts, refreshAdditionalCosts, updateQuantity, deleteCostFromInvoice, tryInitialSave, totalCost, formattedTotalCost, addIngredientCost, addAdditionalCost, addRecipeCost, hideIngredientCostModal, hideAdditionalCostModal, hideRecipeCostModal, addIngredientCostToInvoice, addAdditionalCostToInvoice, addRecipeCostToInvoice, removeIngredientCostFromInvoice, removeAdditionalCostFromInvoice, removeRecipeCostFromInvoice, requestInvoice, saveInvoice, loadMeasurements, VBreadcrumbs, VListAdditionalCost, VListIngredientCost, VListRecipeCost, VModifyIngredientCost, VModifyAdditionalCost, debounce, VSelectFx, VAdditionalMeasurementModal }
}

})